import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../components/base/PageLayout';

const WisdomElegance: React.FC = () => {
  useEffect(() => {
    document.title = '日常の品格 - 執事が教える美しい所作・言葉遣い・整理整頓の哲学｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが提案する「日常の品格」を高める方法。美しい立ち居振る舞い、上品な言葉遣いと話し方、整理整頓の哲学など、毎日の生活の中で実践できる品格向上のヒントを紹介。特別な場だけでなく、日常生活の中で自然と洗練された印象を与える所作と習慣を身につけ、内面から輝く品格ある人になるための執事の知恵をお届けします。');
    }
  }, []);

  const articles = [
    {
      title: '美しい所作',
      excerpt: '日常の動作を上品で洗練されたものにするための技術と心構えを詳しく解説。',
      path: '/wisdom/articles/posture',
      readTime: '10分',
      category: '立ち居振る舞い',
      image: '/images/generated/seq-elegance-posture-butler-hands-1-dd28e9.jpg'
    },
    {
      title: '言葉遣いと話し方',
      excerpt: '品格ある言葉選びと美しい話し方で、相手に好印象を与える技術をご紹介。',
      path: '/wisdom/articles/speech',
      readTime: '8分',
      category: '言葉遣い',
      image: '/images/generated/seq-elegance-speech-butler-hands-2-73ebbe.jpg'
    },
    {
      title: '整理整頓の哲学',
      excerpt: '美しく機能的な空間作りと、心を整える整理整頓の方法を学びます。',
      path: '/wisdom/articles/organization',
      readTime: '9分',
      category: '空間管理',
      image: '/images/generated/seq-elegance-organization-butler-hands-3-1dfa18.jpg'
    }
  ];

  const eleganceAreas = [
    {
      title: '立ち振る舞い',
      description: '歩き方、座り方、立ち方など、基本的な動作を美しく見せる技術',
      icon: 'ri-walk-line'
    },
    {
      title: '言葉遣い',
      description: '相手に敬意を示し、品格を表現する美しい日本語の使い方',
      icon: 'ri-chat-3-line'
    },
    {
      title: '身だしなみ',
      description: 'TPOに応じた適切な装いと、清潔感のある外見の維持',
      icon: 'ri-shirt-line'
    },
    {
      title: '空間作り',
      description: '美しく整った環境を作り、維持するための実践的な方法',
      icon: 'ri-home-4-line'
    },
    {
      title: '時間の使い方',
      description: '余裕のある美しい時間の過ごし方と、効率的な日常管理',
      icon: 'ri-time-line'
    },
    {
      title: '心の在り方',
      description: '内面から滲み出る品格と、穏やかで美しい心の育て方',
      icon: 'ri-heart-line'
    }
  ];

  return (
    <PageLayout hero={<>
<section 
          className="relative py-20 md:py-32 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('/images/gakken/gk-11.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-2xl md:text-6xl font-serif mb-4 md:mb-6">
                日常の品格
              </h1>
              <p className="text-sm md:text-2xl max-w-6xl mx-auto leading-relaxed px-5 sm:px-6 lg:px-8">
                毎日の生活に美しさと品格を取り入れ、<br />
                内面から輝く魅力的な人になりましょう
              </p>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-gradient-to-b from-[#f8f8f8] to-white">
        {/* Hero Section */}
        

        {/* Introduction */}
        <section className="py-12 md:py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-6 md:mb-8">
                品格ある生き方
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="text-sm md:text-xl mb-4 md:mb-6">
                  真の品格は、特別な場面でのみ発揮されるものではありません。日常の何気ない瞬間、普段の立ち振る舞い、そして心の在り方から自然に滲み出るものです。
                </p>
                <p className="text-sm md:text-lg mb-4 md:mb-6">
                  品格ある生き方とは、自分自身を大切にし、周囲の人々への配慮を忘れず、美しいものを愛でる心を持つことです。これは決して難しいことではなく、日々の小さな心がけの積み重ねによって身につけることができます。
                </p>
                <p className="text-sm md:text-lg">
                  ここでは、日常生活の中で実践できる品格向上の方法を、具体的で実用的なアドバイスとともにお伝えしていきます。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Elegance Areas */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                品格を磨く6つの分野
              </h2>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
                日常生活の様々な場面で品格を表現するための実践的な方法
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {eleganceAreas.map((area, index) => (
                <div key={index} className="bg-white p-6 md:p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-4 md:mb-6 bg-[#917c50]/10 rounded-full">
                    <i className={`${area.icon} text-xl md:text-2xl text-[#917c50]`}></i>
                  </div>
                  <h3 className="text-base md:text-xl font-serif text-gray-900 mb-3 md:mb-4 text-center">
                    {area.title}
                  </h3>
                  <p className="text-xs md:text-base text-gray-600 leading-relaxed text-center">
                    {area.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Daily Practices */}
        <section className="py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                日々の実践
              </h2>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
                品格ある生活を送るための具体的な習慣
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div>
                <h3 className="text-lg md:text-2xl font-serif text-gray-900 mb-4 md:mb-6">朝の習慣</h3>
                <div className="space-y-3 md:space-y-4">
                  {[
                    '早起きして心に余裕を持つ',
                    '身だしなみを丁寧に整える',
                    '感謝の気持ちで一日を始める',
                    '美しい姿勢を意識する'
                  ].map((practice, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-[#917c50] rounded-full flex-shrink-0"></div>
                      <span className="text-xs md:text-base text-gray-700">{practice}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg md:text-2xl font-serif text-gray-900 mb-4 md:mb-6">日中の心がけ</h3>
                <div className="space-y-3 md:space-y-4">
                  {[
                    '相手の立場に立って考える',
                    '丁寧な言葉遣いを心がける',
                    '周囲への配慮を忘れない',
                    '美しいものに目を向ける'
                  ].map((practice, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-[#917c50] rounded-full flex-shrink-0"></div>
                      <span className="text-xs md:text-base text-gray-700">{practice}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg md:text-2xl font-serif text-gray-900 mb-4 md:mb-6">夜の振り返り</h3>
                <div className="space-y-3 md:space-y-4">
                  {[
                    '一日の出来事を静かに振り返る',
                    '感謝できることを見つける',
                    '明日への準備を整える',
                    '心を落ち着けて休む'
                  ].map((practice, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-[#917c50] rounded-full flex-shrink-0"></div>
                      <span className="text-xs md:text-base text-gray-700">{practice}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg md:text-2xl font-serif text-gray-900 mb-4 md:mb-6">継続的な成長</h3>
                <div className="space-y-3 md:space-y-4">
                  {[
                    '新しいことを学び続ける',
                    '自分の行動を客観視する',
                    '他者から学ぶ姿勢を持つ',
                    '内面の美しさを磨く'
                  ].map((practice, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-[#917c50] rounded-full flex-shrink-0"></div>
                      <span className="text-xs md:text-base text-gray-700">{practice}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                関連記事
              </h2>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
                日常の品格を高めるための実践的な記事
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {articles.map((article, index) => (
                <Link
                  key={index}
                  to={article.path}
                  className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="relative h-48 bg-gradient-to-br from-amber-100 to-amber-200">
                    <img 
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-amber-700">
                        {article.category}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-black/20 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-base md:text-xl font-serif text-gray-900 mb-3 group-hover:text-[#917c50] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center text-[#917c50] group-hover:text-[#7a6940]">
                      <span className="text-xs md:text-sm font-medium">記事を読む</span>
                      <i className="ri-arrow-right-line ml-2 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-gradient-to-r from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl md:text-4xl font-serif text-white mb-4 md:mb-6">
              品格ある生き方を始めませんか
            </h2>
            <p className="text-sm md:text-xl text-gray-300 mb-6 md:mb-8 leading-relaxed px-5 sm:px-6 lg:px-8">
              個人レッスンやマナー講座を通じて、日常に活かせる品格向上の技術をお伝えします
            </p>
            <Link
              to="/services"
              className="inline-block bg-[#917c50] text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-medium hover:bg-[#7a6940] transition-colors whitespace-nowrap text-sm md:text-base"
            >
              レッスン詳細を見る
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default WisdomElegance;
